import { useState } from 'react';
import { useAuthContext } from '@/contexts/AuthContext';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Input } from '@/components/ui/input';

export function ApiKeyCard() {
  const { agentName, regenerateApiKey } = useAuthContext();
  // newApiKey is only set after regeneration (only time it's visible)
  const [newApiKey, setNewApiKey] = useState<string | null>(null);
  const [isRegenerating, setIsRegenerating] = useState(false);
  const [copied, setCopied] = useState(false);
  const [showConfirm, setShowConfirm] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // No agent claimed yet - don't show API key section
  if (!agentName) {
    return null;
  }

  const handleCopyApiKey = async () => {
    if (newApiKey) {
      await navigator.clipboard.writeText(newApiKey);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    }
  };

  const handleRegenerateApiKey = async () => {
    if (!showConfirm) {
      setShowConfirm(true);
      return;
    }
    
    try {
      setIsRegenerating(true);
      setError(null);
      const key = await regenerateApiKey();
      setNewApiKey(key);
      setShowConfirm(false);
    } catch (err) {
      console.error('Failed to regenerate API key:', err);
      setError('Failed to regenerate API key');
    } finally {
      setIsRegenerating(false);
    }
  };

  const handleCancelRegenerate = () => {
    setShowConfirm(false);
  };

  return (
    <Card>
      <CardHeader>
        <CardTitle className="flex items-center gap-2">
          <span className="material-symbols-outlined text-primary">key</span>
          API Key
        </CardTitle>
        <CardDescription>
          Use this key to authenticate API requests. Keep it secret!
        </CardDescription>
      </CardHeader>
      <CardContent className="space-y-4">
        {/* Info about API key */}
        {!newApiKey && (
          <div className="p-4 rounded-md bg-muted/50 border border-border">
            <p className="text-sm text-muted-foreground">
              Your API key was shown once when your agent registered. 
              If you've lost it, you can regenerate a new one below.
            </p>
          </div>
        )}

        {/* Error State */}
        {error && (
          <div className="p-4 rounded-md bg-destructive/10 border border-destructive/20">
            <p className="text-sm text-destructive">{error}</p>
          </div>
        )}

        {/* New API Key Display (only after regeneration) */}
        {newApiKey && (
          <>
            <div className="p-3 rounded-md bg-primary/10 border border-primary/20">
              <p className="text-sm text-primary font-medium mb-2">
                New API Key Generated! Copy it now - it won't be shown again.
              </p>
            </div>
            <div className="flex gap-2">
              <div className="relative flex-1">
                <Input
                  type="text"
                  value={newApiKey}
                  readOnly
                  className="font-mono text-sm pr-10 bg-muted/50"
                />
              </div>
              <Button
                variant="outline"
                size="icon"
                onClick={handleCopyApiKey}
                title="Copy to clipboard"
              >
                <span className="material-symbols-outlined text-lg">
                  {copied ? 'check' : 'content_copy'}
                </span>
              </Button>
            </div>
            {copied && (
              <p className="text-sm text-primary">Copied to clipboard!</p>
            )}
          </>
        )}

        {/* Regenerate Section */}
        <div className="pt-4 border-t border-border">
          {showConfirm ? (
            <div className="space-y-3">
              <div className="p-3 rounded-md bg-destructive/10 border border-destructive/20">
                <p className="text-sm text-destructive font-medium">
                  Are you sure you want to regenerate your API key?
                </p>
                <p className="text-xs text-muted-foreground mt-1">
                  Your current key will be invalidated immediately. Any agents using it will lose access.
                </p>
              </div>
              <div className="flex gap-2">
                <Button
                  variant="destructive"
                  onClick={handleRegenerateApiKey}
                  disabled={isRegenerating}
                  className="flex-1"
                >
                  {isRegenerating ? (
                    <span className="flex items-center gap-2">
                      <span className="material-symbols-outlined animate-spin text-lg">progress_activity</span>
                      Regenerating...
                    </span>
                  ) : (
                    'Yes, Regenerate Key'
                  )}
                </Button>
                <Button
                  variant="outline"
                  onClick={handleCancelRegenerate}
                  disabled={isRegenerating}
                >
                  Cancel
                </Button>
              </div>
            </div>
          ) : (
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm font-medium">Regenerate API Key</p>
                <p className="text-xs text-muted-foreground">
                  Create a new key if your current one is compromised
                </p>
              </div>
              <Button
                variant="outline"
                onClick={handleRegenerateApiKey}
              >
                <span className="material-symbols-outlined mr-2 text-lg">refresh</span>
                Regenerate
              </Button>
            </div>
          )}
        </div>
      </CardContent>
    </Card>
  );
}
